/**
 * سربرگِ «سلامت» — یک نگاه، همه‌ی سیستم (WHOSUP-264).
 *
 * ═══ چرا وجود دارد ═══
 *
 * سیزده سربرگِ دیگر هر کدام یک گوشه را با جزئیات نشان می‌دهند. این یکی عمداً
 * **به جزئیات نمی‌پردازد**: چند عقربه که بگویند سیستم چقدر سالم است، و کنارِ
 * هر کدام یک پیکان که بگوید نسبت به قبل بهتر شده یا بدتر. عکسی که لود
 * نمی‌شود از سمتِ کرالر دیده نمی‌شود؛ ۶۸۱ رویدادِ بی‌مختصات به‌تنهایی نه خوب
 * است نه بد — وقتی معنی دارد که بدانی دیروز چند تا بود.
 *
 * چهار حوزه: محتوا، سایت و اپ، دیتابیس، کرالر. هر حوزه یک عقربه‌ی بزرگ
 * دارد (میانگینِ عقربه‌های کوچکش) و یک عقربه‌ی کل بالای همه.
 *
 * ═══ تقسیمِ کار ═══
 *
 * مایگریشنِ ۰۱۵۳ فقط **می‌شمارد** و روزی یک عکس نگه می‌دارد؛ حکم — آستانه‌ها،
 * رنگ‌ها، جهتِ «بهتر» — در `core/lib/healthReport.ts` است و با `npm test`
 * آزمودنی. این فایل فقط رندر می‌کند.
 *
 * ═══ چرا «نامعلوم» رنگ ندارد ═══
 *
 * عقربه‌ای که ورودی‌اش نیست (لاگِ فعالیت خالی است، اسکیمای کرالر نیست) به
 * جای عقربه یک «؟» می‌گیرد و می‌گوید چرا. صفرِ «چیزی ثبت نشده» را سبز نشان
 * دادن دقیقاً همان خرابیِ بی‌صدایی است که این صفحه برای دیدنش هست.
 *
 * ═══ چرا SVG و نه یک کتابخانه ═══
 *
 * یک کمانِ سه‌رنگ و یک خطِ عقربه است؛ هیچ وابستگیِ تازه‌ای نمی‌ارزد. رنگ‌ها
 * از توکن‌ها می‌آیند (`--danger` / `--warn` / `--accent`) و **هیچ‌جا تنها
 * حاملِ معنی نیستند**: زیرِ هر عقربه حکم با کلمه نوشته شده (سالم / مشکوک /
 * خراب) و عدد کنارش است.
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import { describeError } from '@/core/lib/report';
import {
  GROUP_GUIDE,
  LEVEL_LABEL,
  OVERALL_GUIDE,
  buildHealthReport,
  describeBounds,
  formatDelta,
  formatValue,
  type CityBreakdown,
  type Gauge as GaugeData,
  type GaugeGuide,
  type GroupReport,
  type Level,
} from '@/core/lib/healthReport';
import { Hint } from '@/ui/Hint';
import { Modal } from '@/ui/Modal';
import { fmtNum as fmt } from '@/ui/Num';

/**
 * متنِ راهنما با تکه‌های کد: هرچه بینِ دو بک‌تیک است (دستور، نامِ جدول،
 * کوئری) چپ‌به‌راست و با فونتِ کد رندر می‌شود تا بشود عیناً کپی‌اش کرد —
 * در متنِ راست‌به‌چپ، یک دستورِ شل بدونِ این جهتش به هم می‌ریزد.
 */
function Rich({ text }: { text: string }) {
  const parts = text.split('`');
  return (
    <>
      {parts.map((p, i) => (i % 2 === 1
        ? <code key={i} className="mono ltr guide-code">{p}</code>
        : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
}

/**
 * پنجره‌ی راهنمای یک عقربه: یعنی چه، مثالِ واقعی، چه را چک کنم، چطور
 * درستش کنم. متن‌ها در `core/lib/healthReport.ts` (`guide`) است، کنارِ
 * آستانه‌ها — تا هرکه آستانه‌ای را عوض می‌کند راهنمایش را هم ببیند.
 */
/**
 * چند ردیفِ واقعی پشتِ عدد (`admin_health_samples`، مایگریشن 0154). فقط با
 * باز شدنِ پنجره خوانده می‌شود، نه با صفحه — نوزده فهرستی که کسی بازشان
 * نمی‌کند هزینه‌ی بی‌خود است.
 */
function Samples({ gaugeKey, caption }: { gaugeKey: string; caption: string }) {
  const q = useQuery({
    queryKey: ['health-samples', gaugeKey],
    queryFn: () => opsRepo.healthSamples(gaugeKey),
  });
  const rows = q.data?.rows ?? [];
  return (
    <>
      <h4>نمونه‌های واقعی</h4>
      <p className="dim"><Rich text={caption} /></p>
      {q.isLoading ? <p className="dim">…</p> : null}
      {q.error ? <p className="err">{describeError(q.error)}</p> : null}
      {q.data && rows.length === 0 ? <p className="dim">الان هیچ ردیفی نیست.</p> : null}
      {rows.length > 0 ? (
        <div className="scrollx">
          <table className="tbl guide-samples">
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    {r.url
                      ? <a href={r.url} target="_blank" rel="noreferrer" dir="auto">{r.title}</a>
                      : <span dir="auto">{r.title}</span>}
                    {r.detail ? <div className="dim" dir="auto">{r.detail}</div> : null}
                  </td>
                  <td className="num">{r.n != null ? `${fmt(r.n)}×` : ''}</td>
                  <td className="num dim">{r.at ? formatDateTime(r.at) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

function GuideModal({ title, guide, gaugeKey, now, bounds, onClose }: {
  title: string; guide: GaugeGuide; gaugeKey?: string; now?: string; bounds?: string | null;
  onClose: () => void;
}) {
  // Escape هم می‌بندد — پنجره‌ای که فقط با کلیک بسته شود برای کیبورد تله است.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Modal title={title} onClose={onClose}>
      <div className="guide">
        {now ? <p className="guide-now">{now}</p> : null}
        {bounds ? <p className="guide-bounds dim">{bounds}</p> : null}
        <h4>یعنی چه؟</h4>
        <p><Rich text={guide.meaning} /></p>
        <h4>در دنیای واقعی</h4>
        <p><Rich text={guide.example} /></p>
        <h4>اگر زرد یا قرمز بود، چه چیزی را چک کنم</h4>
        <ol>{guide.check.map((s, i) => <li key={i}><Rich text={s} /></li>)}</ol>
        <h4>چطور درستش کنم</h4>
        <ol>{guide.fix.map((s, i) => <li key={i}><Rich text={s} /></li>)}</ol>
        {guide.links?.length ? (
          <>
            <h4>برو به</h4>
            {/* تبِ تازه: صفحه‌ی سلامت و همین پنجره سرِ جایشان می‌مانند. */}
            <p className="guide-links">
              {guide.links.map((l) => (
                <a key={l.href} className="chip" href={l.href} target="_blank" rel="noreferrer">
                  {l.label} ↗
                </a>
              ))}
            </p>
          </>
        ) : null}
        {guide.samples && gaugeKey ? <Samples gaugeKey={gaugeKey} caption={guide.samples} /> : null}
      </div>
      <button className="btn block" type="button" onClick={onClose}>بستن</button>
    </Modal>
  );
}

/**
 * دکمه‌ی «i» گوشه‌ی هر کارتِ عقربه. دکمه‌ی واقعی است (نه `title`): متنِ
 * راهنما چند بند است و در تولتیپِ مرورگر خوانده نمی‌شود، و روی لمسی
 * تولتیپ اصلاً باز نمی‌شود.
 */
function GuideButton(props: {
  title: string; guide: GaugeGuide; gaugeKey?: string; now?: string; bounds?: string | null;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="gauge-info" type="button" onClick={() => setOpen(true)}
        aria-label={`راهنمای «${props.title}»`} title="این عقربه یعنی چه و اگر خراب بود چه کنم">
        i
      </button>
      {open ? <GuideModal {...props} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/**
 * کمانِ نیم‌دایره با عقربه. `score` ۰..۱۰۰؛ `null` یعنی نامعلوم — کمان
 * خاکستری و بی‌عقربه.
 *
 * چپ صفر است و راست صد، مثلِ هر سرعت‌سنجی؛ برای همین SVG صریح چپ‌به‌راست
 * می‌ماند و ارثِ `rtl` سند را نمی‌گیرد.
 */
function Dial({ score, level, size = 120 }: { score: number | null; level: Level; size?: number }) {
  const w = size;
  const h = size * 0.58;
  const cx = w / 2;
  const cy = h - 6;
  const r = w / 2 - 8;
  const stroke = Math.max(6, size / 12);

  // زاویه‌ی یک امتیاز، بر حسبِ رادیان: ۰ → چپ (π)، ۱۰۰ → راست (۰).
  const angle = (s: number) => Math.PI - (s / 100) * Math.PI;
  const point = (s: number, rad = r) => ({
    x: cx + rad * Math.cos(angle(s)),
    y: cy - rad * Math.sin(angle(s)),
  });
  const arc = (from: number, to: number) => {
    const a = point(from);
    const b = point(to);
    return `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} A ${r} ${r} 0 0 1 ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
  };

  // سه نوارِ رنگ همان مرزهای LEVEL_BANDS در core هستند — عقربه هرجا بایستد،
  // رنگِ زیرش همان حکمی است که کلمه‌ی زیرِ عقربه می‌گوید.
  const bands: { from: number; to: number; cls: string }[] = [
    { from: 0, to: 35, cls: 'bad' },
    { from: 35, to: 70, cls: 'warn' },
    { from: 70, to: 100, cls: 'ok' },
  ];
  const tip = score == null ? null : point(score, r - stroke / 2 - 2);

  return (
    <svg className={`dial ${level}`} width={w} height={h} viewBox={`0 0 ${w} ${h}`}
      role="img" aria-label={score == null ? 'نامعلوم' : `${score} از ۱۰۰`}>
      {bands.map((b) => (
        <path key={b.cls} className={`band ${b.cls}${score == null ? ' off' : ''}`}
          d={arc(b.from, b.to)} strokeWidth={stroke} />
      ))}
      {tip ? (
        <>
          <line className="needle" x1={cx} y1={cy} x2={tip.x.toFixed(2)} y2={tip.y.toFixed(2)}
            strokeWidth={Math.max(2, size / 50)} strokeLinecap="round" />
          <circle className="pivot" cx={cx} cy={cy} r={Math.max(3, size / 30)} />
        </>
      ) : (
        <text className="unknown" x={cx} y={cy - r * 0.3} textAnchor="middle">؟</text>
      )}
    </svg>
  );
}

/**
 * خطِ روند: یک نقطه برای هر عکسِ روزانه. نقطه‌های تهی جا می‌افتند و خط
 * می‌شکند — «نبود» باید از «صفر» جدا دیده شود.
 */
function Spark({ series, lowerIsBetter }: {
  series: { day: string; value: number | null }[];
  lowerIsBetter: boolean | null;
}) {
  // ∞ (بارِ شهرِ بی رویدادِ تازه) روی خط جا ندارد؛ آن روز خالی می‌ماند.
  const pts = series.filter((p) => p.value != null && Number.isFinite(p.value)) as { day: string; value: number }[];
  if (pts.length < 2) return null;
  const w = 120;
  const h = 26;
  const vals = pts.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const x = (i: number) => (i / (series.length - 1)) * (w - 2) + 1;
  const y = (v: number) => h - 2 - ((v - min) / span) * (h - 4);

  // خط را در جای نقطه‌های تهی می‌شکنیم، پس چند تکه می‌شود.
  const segments: string[] = [];
  let cur: string[] = [];
  series.forEach((p, i) => {
    if (p.value == null || !Number.isFinite(p.value)) {
      if (cur.length) segments.push(cur.join(' '));
      cur = [];
    } else {
      cur.push(`${x(i).toFixed(1)},${y(p.value).toFixed(1)}`);
    }
  });
  if (cur.length) segments.push(cur.join(' '));

  const first = pts[0].value;
  const last = pts[pts.length - 1].value;
  const tone = lowerIsBetter == null || first === last ? ''
    : ((lowerIsBetter ? last < first : last > first) ? ' better' : ' worse');

  return (
    <svg className={`spark${tone}`} width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img">
      <title>{`${series.length} عکسِ روزانه، از ${series[0].day} تا ${series[series.length - 1].day}`}</title>
      {segments.map((d, i) => <polyline key={i} points={d} />)}
    </svg>
  );
}

/** پیکانِ «نسبت به قبل»، با کلمه — رنگ تنها حاملِ معنی نیست. */
function TrendLine({ g }: { g: GaugeData }) {
  const t = g.trend;
  if (t.prevDay == null) {
    return <div className="trend dim">تاریخچه از امروز شروع شد؛ از فردا مقایسه دارد.</div>;
  }
  if (t.delta == null) {
    return <div className="trend dim">نسبت به {t.prevDay}: سنجیدنی نیست.</div>;
  }
  const word = t.better == null ? (t.delta === 0 ? 'بی‌تغییر' : 'تغییر')
    : (t.better ? 'بهتر' : 'بدتر');
  const arrow = t.delta === 0 ? '＝' : (t.delta > 0 ? '▲' : '▼');
  const cls = t.better == null ? '' : (t.better ? ' better' : ' worse');
  return (
    <div className={`trend${cls}`}
      title={`${t.prevDay}: ${formatValue(t.prev, g.unit)} → امروز: ${formatValue(g.value, g.unit)}`}>
      <span className="arrow">{arrow}</span>
      {' '}{word} از {t.prevDay}
      {t.delta !== 0 ? <span className="delta"> ({formatDelta(t.delta, g.unit)})</span> : null}
    </div>
  );
}

/** یک عقربه‌ی کوچک با عدد، حکم، روند و خطِ تاریخچه. */
function GaugeCard({ g }: { g: GaugeData }) {
  return (
    <div className={`card gauge ${g.level}`}>
      <GuideButton title={g.label} guide={g.guide} gaugeKey={g.key}
        now={`الان: ${formatValue(g.value, g.unit)} — ${g.scored ? LEVEL_LABEL[g.level] : 'بی‌حکم؛ فقط روند'}`}
        bounds={g.bounds ? describeBounds(g.bounds, g.unit) : null} />
      {g.scored ? <Dial score={g.score} level={g.level} size={120} /> : null}
      <div className="gauge-val">{formatValue(g.value, g.unit)}</div>
      {g.note ? <div className="dim">{g.note}</div> : null}
      <div className="gauge-k"><Hint text={g.label} help={g.help} /></div>
      {g.scored ? (
        <div className={`gauge-verdict ${g.level}`}>
          {LEVEL_LABEL[g.level]}
          {g.score != null ? <span className="dim"> · {g.score}</span> : null}
        </div>
      ) : (
        <div className="gauge-verdict dim">بی‌حکم؛ فقط روند</div>
      )}
      {g.unknownWhy ? <p className="gauge-why">{g.unknownWhy}</p> : <TrendLine g={g} />}
      <Spark series={g.series} lowerIsBetter={g.lowerIsBetter} />
      {g.cities ? <CityPair g={g} cities={g.cities} /> : null}
    </div>
  );
}

/**
 * جفتِ «بدترین شهر» زیرِ عقربه‌ی «کل» (WHOSUP-303): همان عدد برای بدترین
 * شهرِ واجدِ شرط، نامِ شهر، «۷ از ۹ شهر سالم‌اند»، و فهرستِ همه‌ی شهرها از
 * بد به خوب فقط با «بیشتر». کدام شهر واجد است و رنگ‌ها در core است.
 */
function CityPair({ g, cities }: { g: GaugeData; cities: CityBreakdown }) {
  const w = cities.worst;
  // همان TrendLine، با عدد و روندِ بدترین شهر.
  const worstAsGauge: GaugeData = { ...g, value: w.value, score: w.score, level: w.level, trend: w.trend };
  return (
    <div className="gauge-city">
      <Dial score={w.score} level={w.level} size={100} />
      <div className="gauge-val">{formatValue(w.value, g.unit)}</div>
      <div className="gauge-k">بدترین شهر{w.city ? <>: <b dir="auto">{w.city}</b></> : null}</div>
      <div className={`gauge-verdict ${w.level}`}>
        {LEVEL_LABEL[w.level]}
        {w.score != null ? <span className="dim"> · {w.score}</span> : null}
      </div>
      {w.unknownWhy ? <p className="gauge-why">{w.unknownWhy}</p> : <TrendLine g={worstAsGauge} />}
      <Spark series={w.series} lowerIsBetter={g.lowerIsBetter} />
      {cities.total > 0 ? (
        <div className="dim">{fmt(cities.healthy)} از {fmt(cities.total)} شهر سالم‌اند</div>
      ) : null}
      {cities.total > 0 ? (
        <details>
          <summary>بیشتر</summary>
          <table className="tbl">
            <tbody>
              {cities.cities.map((c) => (
                <tr key={`${c.city}|${c.country_code}`}>
                  <td dir="auto">{c.city} {c.country_code ? <span className="dim ltr">{c.country_code}</span> : null}</td>
                  <td className="num">{formatValue(c.value, g.unit)}</td>
                  <td className={`gauge-verdict ${c.level}`}>{LEVEL_LABEL[c.level]}</td>
                  <td className="num dim">{fmt(c.size)} {cities.sizeUnit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ) : null}
    </div>
  );
}

/** عقربه‌ی بزرگِ یک حوزه (یا کل). */
function BigDial({ label, help, guide, score, level, sub }: {
  label: string; help?: string; guide: GaugeGuide; score: number | null; level: Level; sub?: React.ReactNode;
}) {
  return (
    <div className={`card gauge big ${level}`}>
      <GuideButton title={label} guide={guide}
        now={`الان: ${score == null ? '—' : `${score} از ۱۰۰`} — ${LEVEL_LABEL[level]}`}
        bounds="امتیازِ ۷۰ به بالا سالم، ۳۵ تا ۷۰ مشکوک، زیرِ ۳۵ خراب." />
      <Dial score={score} level={level} size={170} />
      <div className="gauge-val">{score == null ? '—' : `${score}`}<span className="dim"> / 100</span></div>
      <div className="gauge-k"><Hint text={label} help={help} /></div>
      <div className={`gauge-verdict ${level}`}>{LEVEL_LABEL[level]}</div>
      {sub ? <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

function GroupSection({ grp }: { grp: GroupReport }) {
  const unknown = grp.gauges.filter((g) => g.scored && g.level === 'unknown').length;
  return (
    <div className="card">
      <h2>
        <Hint text={grp.label} help={grp.help} />
        <span className={`chip gauge-chip ${grp.level}`} style={{ marginInlineStart: 8 }}>
          {LEVEL_LABEL[grp.level]}{grp.score != null ? ` · ${grp.score}` : ''}
        </span>
        {unknown > 0 ? (
          <span className="dim" style={{ fontSize: 12, marginInlineStart: 8 }}>
            {fmt(unknown)} عقربه نامعلوم و بیرونِ میانگین
          </span>
        ) : null}
      </h2>
      <div className="gauges">
        {grp.gauges.map((g) => <GaugeCard key={g.key} g={g} />)}
      </div>
    </div>
  );
}

/** «تازه» یا «N دقیقه پیش» — کهنگیِ عکسی که صفحه نشان می‌دهد (WHOSUP-324). */
function ageMinutes(takenAt: string | undefined, asOf: string): string {
  if (!takenAt) return 'تازه';
  const m = Math.round((Date.parse(asOf) - Date.parse(takenAt)) / 60_000);
  return m < 1 ? 'تازه' : `${fmt(m)} دقیقه پیش`;
}

export function HealthTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['health'], queryFn: () => opsRepo.health() });
  const [refreshing, setRefreshing] = useState(false);
  const [refreshErr, setRefreshErr] = useState<string | null>(null);
  const refresh = async () => {
    setRefreshing(true);
    setRefreshErr(null);
    try {
      qc.setQueryData(['health'], await opsRepo.health(true));
    } catch (e) {
      setRefreshErr(describeError(e));
    } finally {
      setRefreshing(false);
    }
  };

  if (q.isLoading) return <p className="dim">…</p>;
  // ⚠ نه String(q.error): خطای PostgREST یک شیءِ ساده است و String ازش
  // «[object Object]» می‌سازد — همان درسِ سربرگِ «عکس‌ها» وقتی مایگریشنش
  // روی دیتابیس نبود.
  if (q.error) return <div className="card"><p className="err">{describeError(q.error)}</p></div>;
  if (!q.data) return null;

  const rep = buildHealthReport(q.data);
  const f = q.data.now;

  return (
    <>
      <p className="note legend">
        هر عقربه <b>الان</b> را می‌گوید و پیکانِ زیرش <b>نسبت به آخرین روزِ قبل</b> را.
        هر روز یک عکس گرفته می‌شود (ساعتِ ۱۴ به وقتِ سرور، و وقتی این صفحه
        باز شود و عکسِ امروز کهنه‌تر از یک ساعت باشد). {rep.comparedTo
          ? <>مقایسه با <span className="ltr">{rep.comparedTo}</span>؛ {fmt(rep.snapshots)} عکس در تاریخچه.</>
          : <>این اولین عکس است — از فردا پیکان‌ها معنی پیدا می‌کنند.</>}
        {' '}آستانه‌ها در <span className="mono">core/lib/healthReport.ts</span> است، نه در SQL.
        {' '}عددها حساب‌شده در <span className="ltr">{formatDateTime(q.data.taken_at ?? rep.asOf)}</span>
        {' '}({ageMinutes(q.data.taken_at, rep.asOf)}).
        {' '}<button className="btn" type="button" disabled={refreshing} onClick={() => void refresh()}>
          {refreshing ? 'در حالِ حساب…' : 'حسابِ دوباره'}
        </button>
        {refreshErr ? <span className="err"> {refreshErr}</span> : null}
      </p>

      <div className="grid n5 gauges-top">
        <BigDial label="کلِ سیستم" guide={OVERALL_GUIDE} score={rep.overall.score} level={rep.overall.level}
          help="ضعیف‌ترینِ چهار حوزه، نه میانگینشان — سیستم همان‌قدر سالم است که بیمارترین تکه‌اش. حوزه‌ای که هیچ عقربه‌ی سنجیدنی‌ای ندارد شمرده نمی‌شود."
          sub={<>{fmt(f.content.live_events)} رویدادِ زنده · {fmt(f.app.sessions_7d)} نشست در ۷ روز</>} />
        {rep.groups.map((g) => (
          <BigDial key={g.key} label={g.label} help={g.help} guide={GROUP_GUIDE[g.key]}
            score={g.score} level={g.level} />
        ))}
      </div>

      {rep.groups.map((g) => <GroupSection key={g.key} grp={g} />)}

      <div className="card">
        <h2>
          <Hint text="چند واقعیتِ بی‌حکم"
            help="اعدادی که عقربه نشدند ولی خواندنشان کنارِ عقربه‌ها معنی دارد. همه از همان عکسِ امروزند." />
        </h2>
        <div className="scrollx">
          <table className="tbl">
            <tbody>
              <tr><td>رویدادِ تازه</td>
                <td className="num">{fmt(f.content.created_24h)} در ۲۴ ساعت · {fmt(f.content.created_7d)} در ۷ روز</td></tr>
              <tr><td>عکسِ «غیر معتبر» علامت‌خورده</td><td className="num">{fmt(f.content.invalid_images)}</td></tr>
              <tr><td>خطاهای ۷ روز · کاربرِ درگیر</td>
                <td className="num">{fmt(f.app.errors_7d)} · {fmt(f.app.error_users_7d)}</td></tr>
              <tr><td>آخرین خطا</td>
                <td className="num">{f.app.last_error_at ? formatDateTime(f.app.last_error_at) : '—'}</td></tr>
              <tr><td>نشست‌های ۷ روز · وب / موبایل · آدم</td>
                <td className="num">{fmt(f.app.web_sessions_7d)} / {fmt(f.app.mobile_sessions_7d)} · {fmt(f.app.people_7d)}</td></tr>
              <tr><td>اتصال‌های باز / سقف</td>
                <td className="num">{fmt(f.db.connections)} / {fmt(f.db.max_connections)}</td></tr>
              <tr><td>لاگِ فعالیت · ۷ روز</td>
                <td className="num">{fmt(f.db.devlog_rows_7d)} ردیف · {fmt(f.db.queries_7d)} کوئری · {fmt(f.db.slow_queries_7d)} کندتر از ۲ ثانیه</td></tr>
              {f.db.admin_queries_7d != null ? (
                <tr><td>درخواست‌های ادمین · ۷ روز</td>
                  <td className="num">{fmt(f.db.admin_queries_7d)} کوئری · {fmt(f.db.admin_slow_queries_7d ?? 0)} کندتر از ۲ ثانیه</td></tr>
              ) : null}
              <tr><td>آخرین مایگریشنِ ثبت‌شده</td>
                <td className="num">{f.db.last_migration
                  ? <span className="mono ltr">{f.db.last_migration}</span> : '—'}</td></tr>
              <tr><td>اجراهای کرون · ۲۴ ساعت</td>
                <td className="num">{f.db.cron_runs_24h != null
                  ? `${fmt(f.db.cron_runs_24h)} · ${fmt(f.db.cron_failed_24h ?? 0)} شکست` : '—'}</td></tr>
              {f.crawler.present ? (
                <>
                  <tr><td>آخرین اجرای کاملِ کرالر</td>
                    <td className="num">
                      {f.crawler.last_run_started_at ? formatDateTime(f.crawler.last_run_started_at) : '—'}
                      {f.crawler.last_run_status
                        ? <> · <span className={f.crawler.last_run_status === 'succeeded' ? '' : 'err'}>
                            {f.crawler.last_run_status}</span></>
                        : null}
                      {f.crawler.last_run_full === false
                        ? <> · <span className="err">هیچ اجرای کاملی نیست؛ این آخرین ناقص است</span></>
                        : null}
                    </td></tr>
                  <tr><td>همان اجرا · فاز / صفحه / منتشرشده</td>
                    <td className="num">{fmt(f.crawler.stage_runs)} / {fmt(f.crawler.fetch_requests_run)} / {fmt(f.crawler.published_run)}</td></tr>
                </>
              ) : (
                <tr><td>کرالر</td><td className="dim">این دیتابیس اسکیمای crawler را ندارد.</td></tr>
              )}
              <tr><td>صفِ ادمین · در صف / شکست‌خورده در ۷ روز</td>
                <td className="num">{fmt(f.crawler.jobs_queued)} / {fmt(f.crawler.jobs_failed_7d)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <Exclusions />
    </>
  );
}

/**
 * «حذف از آمار» (مایگریشن 0159): شهر یا کشوری که تیک بخورد در «بارِ شهر»
 * شمرده نمی‌شود — مثلاً شهرِ حساب‌های آزمایشی. چیزی پاک نمی‌شود؛ برداشتنِ
 * تیک برش می‌گرداند. بعد از هر تغییر خودِ سلامت دوباره خوانده می‌شود تا
 * عکسِ امروز تازه شود.
 */
function Exclusions() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['health-exclusions'], queryFn: () => opsRepo.healthExclusions() });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = async (place: { cityId: number } | { country: string }, on: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      await opsRepo.setHealthExclusion(place, on);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['health-exclusions'] }),
        qc.invalidateQueries({ queryKey: ['health'] }),
      ]);
    } catch (e) {
      setErr(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  if (q.isLoading) return null;
  if (q.error) return <div className="card"><p className="err">{describeError(q.error)}</p></div>;
  if (!q.data) return null;

  const offCities = new Set(q.data.excluded.map((x) => x.city_id).filter((x): x is number => x != null));
  const offCountries = new Set(q.data.excluded.map((x) => x.country_code).filter((x): x is string => x != null));
  const countries = [...new Set([...q.data.cities.map((c) => c.country_code), ...offCountries])].sort();

  return (
    <div className="card">
      <h2>
        <Hint text="حذف از آمار"
          help="شهر یا کشوری که تیک بخورد در «بارِ شهر» شمرده نمی‌شود. چیزی پاک نمی‌شود؛ برداشتنِ تیک برش می‌گرداند." />
      </h2>
      {err ? <p className="err">{err}</p> : null}
      <p className="dim">کشورها</p>
      <div>
        {countries.map((cc) => (
          <label key={cc} style={{ marginInlineEnd: 12 }}>
            <input type="checkbox" disabled={busy} checked={offCountries.has(cc)}
              onChange={(e) => toggle({ country: cc }, e.target.checked)} />
            {' '}<span className="ltr">{cc}</span>
          </label>
        ))}
      </div>
      <p className="dim">شهرهایی که کاربر دارند</p>
      <div className="scrollx">
        <table className="tbl">
          <tbody>
            {q.data.cities.map((c) => {
              const byCountry = offCountries.has(c.country_code);
              return (
                <tr key={c.id}>
                  <td>
                    <label>
                      <input type="checkbox" disabled={busy || byCountry}
                        checked={byCountry || offCities.has(c.id)}
                        onChange={(e) => toggle({ cityId: c.id }, e.target.checked)} />
                      {' '}{c.name} <span className="dim ltr">{c.country_code}</span>
                    </label>
                  </td>
                  <td className="num">{fmt(c.users)} کاربر{byCountry ? <span className="dim"> · با کشورش حذف شده</span> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
